"use client";
import { useEffect, useRef, useState } from "react";
import useSWR from "swr";
import {
	fetchMarket,
	fetchTopLiquidityMarkets,
} from "../../../api/terminalApi";
import type { GroupedMarket } from "../../../types/terminal";
import { groupMarkets } from "../../terminal/selectors/markets";

interface MarketPickerProps {
	selected: GroupedMarket | null;
	onSelect: (market: GroupedMarket) => void;
	initialMarketId?: string | null;
}

// Alive markets only (close=false). The default view shows the top 20 by
// volume; typing a query searches the full market universe on the backend
// instead of just this pre-fetched slice.
const DISPLAY_LIMIT = 20;
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_RESULTS_LIMIT = 50;

export function MarketPicker({
	selected,
	onSelect,
	initialMarketId,
}: MarketPickerProps) {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const [debouncedQuery, setDebouncedQuery] = useState("");
	// Live-as-you-type search stays scoped to active markets; pressing Enter
	// broadens that same search to also surface resolved/closed ones.
	const [includeClosed, setIncludeClosed] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);
	const searchInputRef = useRef<HTMLInputElement>(null);
	const triedInitialRef = useRef(false);

	useEffect(() => {
		if (open) searchInputRef.current?.focus();
	}, [open]);

	useEffect(() => {
		const timer = setTimeout(
			() => setDebouncedQuery(query),
			SEARCH_DEBOUNCE_MS,
		);
		return () => clearTimeout(timer);
	}, [query]);

	const { data, isLoading } = useSWR(
		["superset-market-picker"],
		async () => {
			const response = await fetchTopLiquidityMarkets(
				1,
				DISPLAY_LIMIT,
				false,
				undefined,
				"volume",
			);
			return groupMarkets(response.data, { sortBy: "volume" });
		},
		{ revalidateOnFocus: false },
	);

	const markets = data ?? [];

	const trimmedQuery = debouncedQuery.trim();
	const { data: searchResults, isLoading: isSearching } = useSWR(
		trimmedQuery ? ["market-search", trimmedQuery, includeClosed] : null,
		async () => {
			const response = await fetchTopLiquidityMarkets(
				1,
				SEARCH_RESULTS_LIMIT,
				includeClosed ? undefined : false,
				trimmedQuery,
			);
			return groupMarkets(response.data, { includeResolved: includeClosed });
		},
		{ revalidateOnFocus: false, keepPreviousData: true },
	);

	useEffect(() => {
		if (selected || markets.length === 0 || triedInitialRef.current) return;
		triedInitialRef.current = true;

		if (initialMarketId) {
			const match = markets.find((m) => m.conditionId === initialMarketId);
			if (match) {
				onSelect(match);
				return;
			}
			// Deep-linked market fell outside the top pool -- resolve it directly
			// so a shared/bookmarked URL still lands on the right market.
			void fetchMarket(initialMarketId).then((raw) => {
				if (raw) {
					onSelect({
						conditionId: String(raw.conditionId ?? initialMarketId),
						question: String(raw.question ?? initialMarketId),
						closed: Boolean(raw.closed),
						outcomes: [],
					});
					return;
				}
				const first = markets[0];
				if (first) onSelect(first);
			});
			return;
		}

		const first = markets[0];
		if (first) onSelect(first);
	}, [markets, selected, initialMarketId, onSelect]);

	useEffect(() => {
		const handleClick = (e: MouseEvent) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(e.target as Node)
			) {
				setOpen(false);
			}
		};
		document.addEventListener("mousedown", handleClick);
		return () => document.removeEventListener("mousedown", handleClick);
	}, []);

	const isSearchMode = query.trim().length > 0;
	const filtered = isSearchMode
		? (searchResults ?? [])
		: markets.slice(0, DISPLAY_LIMIT);
	const showLoading = isSearchMode
		? isSearching && !searchResults
		: isLoading && markets.length === 0;

	return (
		<div className="relative w-full max-w-xl" ref={containerRef}>
			<div
				id="market-picker-caption"
				className="text-[10px] font-mono uppercase tracking-wider text-base-content/50 mb-1"
			>
				Market
			</div>
			<button
				type="button"
				aria-labelledby="market-picker-caption"
				onClick={() => setOpen((v) => !v)}
				className="w-full flex items-center gap-2 text-left px-3 py-2 rounded bg-base-200 border border-base-content/10 text-xs md:text-sm font-mono text-base-content hover:border-primary/40 transition-colors"
			>
				{isLoading && !selected && (
					<span className="inline-block w-3 h-3 shrink-0 rounded-full border-2 border-primary/25 border-t-primary animate-spin" />
				)}
				<span className="truncate">
					{selected
						? selected.question
						: isLoading
							? "Loading markets…"
							: "Select a market…"}
				</span>
			</button>
			{open && (
				<div className="absolute z-30 mt-1 w-full rounded bg-base-200 border border-base-content/10 shadow-xl overflow-hidden">
					<input
						ref={searchInputRef}
						aria-label="Search markets"
						value={query}
						onChange={(e: Event) => {
							setQuery((e.currentTarget as HTMLInputElement).value);
							setIncludeClosed(false);
						}}
						onKeyDown={(e) => {
							if (e.key === "Enter" && query.trim()) {
								setDebouncedQuery(query);
								setIncludeClosed(true);
							}
						}}
						placeholder="Search markets… (Enter to include resolved)"
						className="w-full px-3 py-2 bg-base-300 text-xs font-mono text-base-content outline-none border-b border-base-content/10"
					/>
					{!isSearchMode && markets.length > 0 && (
						<div className="px-3 py-1.5 text-[10px] font-mono text-base-content/35 border-b border-base-content/5">
							Top {Math.min(DISPLAY_LIMIT, markets.length)} by volume — type
							to search all alive markets
						</div>
					)}
					{isSearchMode && !includeClosed && (
						<div className="px-3 py-1.5 text-[10px] font-mono text-base-content/35 border-b border-base-content/5">
							Searching active markets — press Enter to include resolved ones
						</div>
					)}
					{isSearchMode && includeClosed && (
						<div className="px-3 py-1.5 text-[10px] font-mono text-base-content/35 border-b border-base-content/5">
							Including resolved markets
						</div>
					)}
					<div className="max-h-72 overflow-y-auto">
						{showLoading && (
							<div className="flex items-center gap-2 px-3 py-3 text-xs font-mono text-base-content/40">
								<span className="inline-block w-3 h-3 rounded-full border-2 border-primary/25 border-t-primary animate-spin" />
								{isSearchMode ? "Searching…" : "Loading markets…"}
							</div>
						)}
						{!showLoading && filtered.length === 0 && (
							<div className="px-3 py-3 text-xs font-mono text-base-content/40">
								No markets found.
							</div>
						)}
						{filtered.map((m) => (
							<button
								key={m.conditionId}
								type="button"
								onClick={() => {
									onSelect(m);
									setOpen(false);
									setQuery("");
									setIncludeClosed(false);
								}}
								className={`w-full flex items-center gap-2 text-left px-3 py-2 text-xs font-mono hover:bg-primary/10 transition-colors ${
									m.conditionId === selected?.conditionId
										? "text-primary"
										: "text-base-content/80"
								}`}
							>
								<span className="truncate">{m.question}</span>
								{m.closed && (
									<span className="shrink-0 text-[9px] uppercase tracking-wider text-base-content/40 border border-base-content/15 rounded px-1 py-0.5">
										resolved
									</span>
								)}
							</button>
						))}
					</div>
				</div>
			)}
		</div>
	);
}
